import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight } from 'lucide-react';
import BlogCard from './BlogCard';
import { fetchLatestPosts } from '../services/blogService';
import './LatestBlogs.css';

/**
 * Latest Blogs Section Component
 * Displays the 3 most recent blog posts from GrowLeads.io
 * Placed after FAQ section on the landing page
 */
const LatestBlogs = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const latestPosts = await fetchLatestPosts(3);
        setPosts(latestPosts);
        setError(null);
      } catch (err) {
        console.error('Failed to load latest blog posts:', err);
        setError('Unable to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // Don't render section if there's an error or no posts
  if (error || (!loading && posts.length === 0)) {
    return null;
  }

  return (
    <section className="latest-blogs-section">
      <div className="container">
        {/* Section Header */}
        <motion.div 
          className="section-header-modern"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="latest-blogs__badge">
            <BookOpen size={16} />
            <span>Resources & Insights</span>
          </div>
          <h2>Latest From the Growleads Blog</h2>
          <p className="latest-blogs__subtitle">Expert guides, strategies, and insights on LinkedIn optimization, AI tools, and B2B growth</p>
        </motion.div>

        {/* Blog Cards Grid */}
        <div className="latest-blogs__grid">
          {loading ? (
            // Loading skeletons
            [...Array(3)].map((_, i) => (
              <div key={i} className="blog-card-skeleton">
                <div className="blog-card-skeleton__image"></div>
                <div className="blog-card-skeleton__content">
                  <div className="blog-card-skeleton__title"></div>
                  <div className="blog-card-skeleton__excerpt"></div>
                  <div className="blog-card-skeleton__meta"></div>
                </div>
              </div>
            ))
          ) : (
            posts.map((post, index) => (
              <BlogCard
                key={post.link || index}
                title={post.title}
                excerpt={post.excerpt}
                pubDate={post.pubDate}
                featuredImage={post.featuredImage}
                link={post.link}
                categories={post.categories}
                author={post.author}
                index={index}
              />
            ))
          )}
        </div>

        {/* View All Button */}
        {!loading && posts.length > 0 && (
          <motion.div 
            className="latest-blogs__cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link to="/blog" className="latest-blogs__btn">
              <span>View All Blog Posts</span>
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default LatestBlogs;
